import { ref } from 'vue';
import { nonEscapeHTMLArrayByObject, nonEscapeHTMLObject } from '~/composables/useCommon';
import { showAlert } from '~/composables/useConfirmDialog';
import { appT } from '~/composables/useAppI18n';
import { useAttachmentApi } from '~/composables/attachment/useAttachmentApi';
import { useQnaBoardApi } from './useQnaBoardApi';

const { fetchBoardList, fetchBoardDetail } = useQnaBoardApi();
const { fetchAtchFileList } = useAttachmentApi();

const QNA_BOARD_ID = 'QNA';

const isQnaModalOpen = ref(false);
const isQnaDetailModalOpen = ref(false);
const qnaList = ref<any[]>([]);
const qnaRecords = ref(0);
const qnaDetail = ref<any>(null);
const qnaAttachmentFiles = ref<any[]>([]);

const qnaFind = ref({
  boardId: QNA_BOARD_ID,
  searchKeyword: '',
  userNm: '',
  page: 1,
  rows: 10,
  seq: '',
});

const buildQnaListParams = () => ({
  boardId: qnaFind.value.boardId,
  searchKeyword: qnaFind.value.searchKeyword,
  userNm: qnaFind.value.userNm,
  page: qnaFind.value.page,
  rows: qnaFind.value.rows,
});

const buildQnaDetailParams = (seq: string) => ({
  boardId: qnaFind.value.boardId,
  seq,
  searchKeyword: qnaFind.value.searchKeyword,
  userNm: qnaFind.value.userNm,
  page: qnaFind.value.page,
  rows: qnaFind.value.rows,
});

const handleSelectQnaList = async (page = qnaFind.value.page) => {
  try {
    qnaFind.value.page = page;
    const res = await fetchBoardList(buildQnaListParams());
    const rows = Array.isArray(res?.rows) ? res.rows : [];
    qnaList.value = nonEscapeHTMLArrayByObject(rows);
    qnaRecords.value = Number(res?.records ?? rows.length);
  } catch (e) {
    console.error(e);
    showAlert(appT('common.error'), appT('common.qnaBoard.listFetchError'));
  }
};

const handleQnaSearch = async () => {
  await handleSelectQnaList(1);
};

const handleQnaPageChange = async (page: number) => {
  await handleSelectQnaList(page);
};

const popQnA = async () => {
  isQnaModalOpen.value = true;
  await handleSelectQnaList(1);
};

const closeQnaDetail = () => {
  isQnaDetailModalOpen.value = false;
  qnaDetail.value = null;
  qnaAttachmentFiles.value = [];
  qnaFind.value.seq = '';
};

const handleQnaDetail = async (row: any) => {
  const seq = row?.seq ?? '';
  if (!seq) return;

  try {
    qnaFind.value.seq = seq;
    const res = await fetchBoardDetail(buildQnaDetailParams(seq));
    qnaDetail.value = nonEscapeHTMLObject(res?.dataVO ?? {});
    isQnaDetailModalOpen.value = true;

    const atchFileId = qnaDetail.value?.atchFileId ?? qnaDetail.value?.atchFileKey ?? '';
    if (atchFileId) {
      const atchRes = await fetchAtchFileList({ atchFileId });
      qnaAttachmentFiles.value = atchRes?.fileList ?? [];
    } else {
      qnaAttachmentFiles.value = [];
    }
  } catch (e) {
    console.error(e);
    showAlert(appT('common.error'), appT('common.qnaBoard.detailFetchError'));
  }
};

export const useQnaBoardStore = () => {
  return {
    isQnaModalOpen,
    isQnaDetailModalOpen,
    qnaList,
    qnaRecords,
    qnaFind,
    qnaDetail,
    qnaAttachmentFiles,
    popQnA,
    handleSelectQnaList,
    handleQnaSearch,
    handleQnaPageChange,
    handleQnaDetail,
    closeQnaDetail,
  };
};
